import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Plus, Pencil, Trash, Buildings, MagnifyingGlass, EnvelopeSimple,
  Phone, MapTrifold, CheckCircle, XCircle, Power, Warning, Hourglass, CreditCard, Prohibit, BellRinging, ArrowsClockwise
} from '@phosphor-icons/react';

// Map Stripe-derived subscription_status -> badge config
const BILLING_STATUS = {
  active:          { label: 'Active',         className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', Icon: CheckCircle },
  trialing:        { label: 'Trial',          className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',           Icon: Hourglass    },
  pending_payment: { label: 'Pending Payment', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',       Icon: CreditCard  },
  past_due:        { label: 'Payment Failed', className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  unpaid:          { label: 'Unpaid',         className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  trial_expired:   { label: 'Trial Expired',  className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
  canceled:        { label: 'Canceled',       className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',           Icon: Prohibit    },
  canceling:       { label: 'Canceling',      className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Hourglass   },
  incomplete:      { label: 'Incomplete',     className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Warning     },
  incomplete_expired: { label: 'Expired',     className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
};

function renderBillingBadge(t) {
  const status = t.subscription_status;
  if (!status) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const cfg = BILLING_STATUS[status] || { label: status, className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20', Icon: Warning };
  const Icon = cfg.Icon;
  return (
    <Badge className={`text-xs ${cfg.className}`} data-testid={`billing-status-${status}`}>
      <Icon size={12} className="mr-1" /> {cfg.label}
    </Badge>
  );
}

export default function TenantsPage() {
  const { apiCall } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState({
    name: '', organization: '', email: '', address: '', mobile_no: '', work_no: ''
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [billingFilter, setBillingFilter] = useState('all');
  const [reminderLoading, setReminderLoading] = useState(null);

  // Change-plan dialog state
  const [planTarget, setPlanTarget] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planSaving, setPlanSaving] = useState(false);

  async function openChangePlan(t) {
    setPlanTarget(t);
    setSelectedPlanId(t.subscription_plan || 'basic');
    try {
      const res = await apiCall('get', '/billing/plans');
      setAvailablePlans(res.data || []);
    } catch (e) {
      setAvailablePlans([]);
      toast.error('Could not load plans.');
    }
  }

  async function handleChangePlan() {
    if (!planTarget || !selectedPlanId) return;
    if (selectedPlanId === planTarget.subscription_plan) {
      toast.error('Tenant is already on this plan.');
      return;
    }
    setPlanSaving(true);
    try {
      // No live Stripe subscription on the target → force local override so
      // we don't touch Stripe at all (test/comp tenants).
      const hasLiveSub = !!planTarget.stripe_subscription_id;
      const res = await apiCall('post', '/billing/set-plan', {
        tenant_id: planTarget.id,
        plan_id: selectedPlanId,
        local_only: !hasLiveSub,
      });
      const msg = res.data?.billing_mode === 'stripe_proration'
        ? `Plan changed via Stripe (${res.data.effective === 'immediate' ? 'effective now' : 'effective next renewal'}).`
        : `Plan set to ${selectedPlanId} (manual override — no charge).`;
      toast.success(msg);
      setPlanTarget(null);
      loadTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change plan.');
    } finally {
      setPlanSaving(false);
    }
  }

  async function handleSendReminder(t) {
    setReminderLoading(t.id);
    try {
      const res = await apiCall('post', '/billing/send-payment-reminder', { tenant_id: t.id });
      if (res.data?.sent) {
        alert(`Payment reminder emailed to ${res.data.to}`);
      } else {
        alert('Reminder logged but email delivery may have failed. Check SendGrid logs.');
      }
      loadTenants();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminderLoading(null);
    }
  }

  useEffect(() => { loadTenants(); }, []);

  // Quick action intent from CommandPalette
  useEffect(() => {
    const intent = sessionStorage.getItem('roofmate_pending_action');
    if (intent === 'new-tenant') {
      sessionStorage.removeItem('roofmate_pending_action');
      setTimeout(() => openNew(), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadTenants() {
    try {
      const res = await apiCall('get', '/tenants');
      setTenants(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleSave() {
    if (!form.name.trim() || !form.organization.trim() || !form.email.trim()) return;
    try {
      if (editTenant) {
        await apiCall('put', `/tenants/${editTenant.id}`, form);
      } else {
        await apiCall('post', '/tenants', form);
      }
      setShowDialog(false);
      setEditTenant(null);
      setForm({ name: '', organization: '', email: '', address: '', mobile_no: '', work_no: '' });
      loadTenants();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to save tenant');
    }
  }

  async function handleDelete(id) {
    try {
      await apiCall('delete', `/tenants/${id}`);
      setDeleteTarget(null);
      loadTenants();
    } catch (e) { console.error(e); }
  }

  async function handleToggleActive() {
    if (!toggleTarget) return;
    const newStatus = toggleTarget.is_active === false ? true : false;
    try {
      await apiCall('put', `/tenants/${toggleTarget.id}`, { is_active: newStatus });
      setToggleTarget(null);
      loadTenants();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to update status');
    }
  }

  function openEdit(t) {
    setEditTenant(t);
    setForm({
      name: t.name, organization: t.organization, email: t.email,
      address: t.address || '', mobile_no: t.mobile_no || '', work_no: t.work_no || ''
    });
    setShowDialog(true);
  }

  function openNew() {
    setEditTenant(null);
    setForm({ name: '', organization: '', email: '', address: '', mobile_no: '', work_no: '' });
    setShowDialog(true);
  }

  const filtered = tenants.filter(t => {
    const matchesSearch = (
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.organization.toLowerCase().includes(search.toLowerCase())
    );
    if (!matchesSearch) return false;
    if (billingFilter === 'all') return true;
    if (billingFilter === 'active') return t.subscription_status === 'active';
    if (billingFilter === 'trial') return t.subscription_status === 'trialing';
    if (billingFilter === 'issues') return ['past_due','unpaid','trial_expired','canceled','pending_payment','incomplete_expired'].includes(t.subscription_status);
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="tenants-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="tenants-heading">
            Tenants
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Manage all tenant organizations</p>
        </div>
        <Button onClick={openNew} className="rounded-md font-medium" data-testid="create-tenant-btn">
          <Plus size={16} className="mr-2" weight="bold" /> New Tenant
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants..." className="pl-9 rounded h-10" data-testid="tenants-search-input" />
        </div>
        <div className="flex items-center gap-1 bg-secondary/50 rounded-md p-1" data-testid="billing-filter">
          {[
            { id: 'all',    label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'trial',  label: 'Trial' },
            { id: 'issues', label: 'Issues' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setBillingFilter(f.id)}
              data-testid={`billing-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${billingFilter === f.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {f.label}
              {f.id === 'issues' && tenants.filter(t => ['past_due','unpaid','trial_expired','canceled','pending_payment','incomplete_expired'].includes(t.subscription_status)).length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {tenants.filter(t => ['past_due','unpaid','trial_expired','canceled','pending_payment','incomplete_expired'].includes(t.subscription_status)).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="shadow-card rounded-lg border-0">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Buildings size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-semibold text-lg">No tenants found</p>
            <p className="text-sm mt-1">Create a new tenant to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-card rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Organization</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Users</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Projects</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Billing</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((t) => (
                <TableRow key={t.id} className="hover:bg-secondary/30" data-testid={`tenant-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium">{t.organization}</div>
                    <div className="text-xs text-muted-foreground">{t.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm flex items-center gap-1"><EnvelopeSimple size={12} /> {t.email}</div>
                    {t.mobile_no && <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={10} /> {t.mobile_no}</div>}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => openChangePlan(t)}
                      title="Change plan"
                      data-testid={`change-plan-badge-${t.id}`}
                      className="group inline-flex items-center gap-1 rounded-md px-1 py-0.5 -mx-1 hover:bg-secondary/70 transition-colors"
                    >
                      <Badge variant="outline" className="text-xs capitalize">{t.subscription_plan || 'free'}</Badge>
                      <ArrowsClockwise size={11} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" weight="bold" />
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">{t.user_count || 0}</TableCell>
                  <TableCell className="text-sm">{t.project_count || 0}</TableCell>
                  <TableCell>{renderBillingBadge(t)}</TableCell>
                  <TableCell>
                    {t.is_active !== false ? (
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">
                        <CheckCircle size={12} className="mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">
                        <XCircle size={12} className="mr-1" /> Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {['past_due','unpaid','trial_expired','canceled','pending_payment','incomplete_expired'].includes(t.subscription_status) && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleSendReminder(t)}
                          disabled={reminderLoading === t.id}
                          className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600"
                          title="Send payment reminder"
                          data-testid={`reminder-tenant-${t.id}`}
                        >
                          <BellRinging size={14} weight={reminderLoading === t.id ? 'fill' : 'duotone'} />
                        </Button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => openChangePlan(t)}
                        className="h-8 w-8 p-0 text-primary hover:text-primary"
                        title="Change plan"
                        data-testid={`change-plan-btn-${t.id}`}
                      >
                        <ArrowsClockwise size={14} weight="bold" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setToggleTarget(t)}
                        className={`h-8 w-8 p-0 ${t.is_active === false ? 'text-emerald-600 hover:text-emerald-600' : 'text-amber-500 hover:text-amber-500'}`}
                        title={t.is_active === false ? 'Activate' : 'Deactivate'}
                        data-testid={`toggle-tenant-${t.id}`}>
                        <Power size={14} weight="bold" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-8 w-8 p-0" data-testid={`edit-tenant-${t.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(t)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" data-testid={`delete-tenant-${t.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Tenant Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="tenant-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editTenant ? 'Edit Tenant' : 'New Tenant'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[13px] font-medium">Name</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Contact name" className="rounded-md" data-testid="tenant-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[13px] font-medium">Organization</Label>
                <Input value={form.organization} onChange={(e) => setForm(p => ({ ...p, organization: e.target.value }))} placeholder="Company name" className="rounded-md" data-testid="tenant-org-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@company.com" className="rounded-md" data-testid="tenant-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Address</Label>
              <Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Business address" className="rounded-md" data-testid="tenant-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[13px] font-medium">Mobile No.</Label>
                <Input value={form.mobile_no} onChange={(e) => setForm(p => ({ ...p, mobile_no: e.target.value }))} className="rounded-md" data-testid="tenant-mobile-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[13px] font-medium">Work No.</Label>
                <Input value={form.work_no} onChange={(e) => setForm(p => ({ ...p, work_no: e.target.value }))} className="rounded-md" data-testid="tenant-work-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} className="rounded-md" data-testid="tenant-save-btn">{editTenant ? 'Save' : 'Create'}</Button>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={!!planTarget} onOpenChange={(open) => { if (!open) { setPlanTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Change Plan — {planTarget?.organization}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-muted-foreground">
              Current plan: <Badge variant="outline" className="ml-1 text-xs capitalize">{planTarget?.subscription_plan || 'free'}</Badge>
              {planTarget?.stripe_subscription_id ? (
                <p className="mt-2 text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                  <Warning size={14} className="mt-0.5 shrink-0" weight="duotone" />
                  <span>This tenant has a live Stripe subscription. Stripe will be updated with proration (upgrade: immediate, downgrade: at next renewal).</span>
                </p>
              ) : (
                <p className="mt-2 text-emerald-600 dark:text-emerald-400 flex items-start gap-1.5">
                  <CheckCircle size={14} className="mt-0.5 shrink-0" weight="duotone" />
                  <span>No live subscription. This is a manual override — no charge will be made and the tenant will be unlocked.</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Select new plan</Label>
              <div className="grid gap-2" data-testid="plan-options">
                {availablePlans.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Loading plans…</p>
                ) : availablePlans.map((p) => {
                  const isSelected = selectedPlanId === p.plan_id;
                  const isCurrent = planTarget?.subscription_plan === p.plan_id;
                  return (
                    <button
                      key={p.plan_id}
                      type="button"
                      onClick={() => setSelectedPlanId(p.plan_id)}
                      data-testid={`plan-option-${p.plan_id}`}
                      className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-left transition-colors ${
                        isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary/40' : 'border-border hover:bg-secondary/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <CreditCard size={16} className="text-primary shrink-0" weight="duotone" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium capitalize">
                            {p.name || `${p.plan_id} plan`}
                            {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">Current</span>}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            ${Number(p.price || 0).toFixed(0)}/mo
                            {p.price_per_user ? ' per user' : ''}
                          </div>
                        </div>
                      </div>
                      {isSelected && <CheckCircle size={16} className="text-primary shrink-0" weight="fill" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanTarget(null)} className="rounded-md" disabled={planSaving}>Cancel</Button>
            <Button
              onClick={handleChangePlan}
              className="rounded-md"
              disabled={planSaving || !selectedPlanId || selectedPlanId === planTarget?.subscription_plan}
              data-testid="change-plan-confirm-btn"
            >
              {planSaving ? 'Saving…' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Tenant"
        description={`Are you sure you want to delete "${deleteTarget?.organization || deleteTarget?.name}"? This will permanently remove the tenant, all associated users, and all their projects. This action cannot be undone.`}
        onConfirm={() => handleDelete(deleteTarget?.id)}
        destructiveLabel="Delete Tenant"
      />

      <ConfirmDeleteDialog
        open={!!toggleTarget}
        onOpenChange={(open) => !open && setToggleTarget(null)}
        title={toggleTarget?.is_active === false ? 'Activate Tenant' : 'Deactivate Tenant'}
        description={toggleTarget?.is_active === false
          ? `Activate "${toggleTarget?.organization}"? All users under this tenant will be able to login again.`
          : `Deactivate "${toggleTarget?.organization}"? All users under this tenant will be blocked from logging in. No data will be deleted.`}
        onConfirm={handleToggleActive}
        destructiveLabel={toggleTarget?.is_active === false ? 'Activate' : 'Deactivate'}
      />
    </div>
  );
}
